// Universal headless mount-point for chart export.
// Spec: .aidev/tasks/headless-charts-sdk-spike/docs/architecture-final.md §5
//
// A single React root drives every chart-type. The Playwright driver injects
// data via window.setChartExportData(data, params, requestId); we look the
// chart up in ExportableCharts and mount the existing component under an
// ExportContext provider so its useExportableChart hook activates.
//
// Failure never reads as silence: whatever the shell cannot resolve becomes an error on the same
// event channel (useChartReadyGuard), and a crash inside the mounted chart becomes one too
// (ExportErrorBoundary).

import { useEffect, useMemo, useState } from 'react'
import type { ComponentType } from 'react'

import {
  clearExportDebug,
  ExportContext,
  ExportableCharts
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'

import { ExportErrorBoundary } from './ExportErrorBoundary'
import { useChartReadyGuard } from './useChartReadyGuard'
import type { ExportPayload } from './types'

// Default export-canvas width (legacy vk/HC default). Fixing the container width
// makes container-measured charts draw once at a deterministic size instead of
// remeasuring the viewport via ResizeObserver. Driver can override per chart.
const EXPORT_WIDTH = 746

type ResolvedChart = {
  Component?: ComponentType<any>
  props?: Record<string, unknown>
  error?: string
}

// What the payload asks for, or why it cannot be drawn. Reporting is left to the guard hook:
// emitting from the render phase would beat the debug snapshot the error message carries.
const resolveChart = (payload: ExportPayload | null): ResolvedChart => {
  if (!payload) return {}

  const entry = ExportableCharts.get(payload.params.chartType)
  if (!entry) return { error: `Unknown chartType: ${payload.params.chartType}` }

  try {
    const Component = entry.resolveComponent?.(payload.data, payload.params) || entry.Component

    return { Component, props: entry.toProps(payload.data, payload.params) }
  } catch (error) {
    return { error: `toProps failed: ${(error as Error).message}` }
  }
}

// The driver's entry point into the page, installed for as long as the shell is mounted.
const useExportPayload = () => {
  const [payload, setPayload] = useState<ExportPayload | null>(null)

  useEffect(() => {
    ;(window as any).setChartExportData = (data: unknown, params: any, requestId: string) => {
      // Cleared before the new tree renders: the guard effect fires only after the children's
      // first commit, and clearing there would wipe the entries a stuck chart never re-records.
      clearExportDebug()
      setPayload({ data, params, requestId })
    }
    return () => {
      delete (window as any).setChartExportData
    }
  }, [])

  return payload
}

export function RenderShell() {
  const payload = useExportPayload()
  const { Component, props, error } = useMemo(() => resolveChart(payload), [payload])

  useChartReadyGuard(payload, error)

  if (!payload || !Component || !props) return null

  return (
    <ExportContext.Provider value={{ requestId: payload.requestId }}>
      {/* Fixed-width export canvas — deterministic width, no ResizeObserver remeasure. */}
      <div style={{ width: Number(payload.params.width) || EXPORT_WIDTH }}>
        {/* key forces unmount+mount per setChartExportData: old hook cleanup runs
            (deletes window.getChartExportPng), new mount installs a fresh one. */}
        <ExportErrorBoundary key={payload.requestId} requestId={payload.requestId}>
          <Component {...props} />
        </ExportErrorBoundary>
      </div>
    </ExportContext.Provider>
  )
}
